import { useEffect, useState } from 'react';
import type { OrgMember, Organization } from '@heybesafe/shared';
import { decryptMemberName } from '../crypto/memberName';
import { base64UrlToBytes } from '../crypto/base64url';
import { getCachedMemberNames, setCachedMemberNames } from '../storage/safe-store';

/**
 * Session memo of already-decrypted names, keyed by orgId.
 *
 * Without it, every visit to a family page re-decrypts from scratch and the UI
 * visibly corrects itself from emails to names — the reported symptom. With it,
 * a re-mount paints synchronously on its first render.
 *
 * In memory only: the plaintext never reaches storage. Purged for an org the
 * moment its family key is gone (lock / logout / user switch) — see the effect
 * below — so a previous user's names can never survive into a new session.
 */
const decryptedMemo = new Map<string, Map<string, string>>();

/** Test seam. Module state outlives a single test file's renders. */
export function __resetMemberNameMemo(): void {
  decryptedMemo.clear();
}

/**
 * One member's already-decrypted name for this session, or undefined.
 *
 * Lets a component that is NOT rendering a roster (MyFamilyNameCard, which
 * only ever needs its own name) seed synchronously from the same memo the
 * roster fills, instead of keeping a second cache in step with this one.
 */
export function getMemoizedMemberName(
  orgId: string | undefined,
  userId: string | undefined,
): string | undefined {
  if (!orgId || !userId) return undefined;
  return decryptedMemo.get(orgId)?.get(userId);
}

interface FamilyKeyAccess {
  getKey(orgId: string): Uint8Array | undefined;
  /** Bumps when a key hydrates. MUST be in the effect deps — `getKey` is a
   *  stable reference and will not re-run the effect on its own. */
  version: number;
}

/**
 * The display name for a member: their decrypted name, else their email.
 *
 * Never returns an empty string. A member with no name, an unhydrated family
 * key, or a blob that failed to decrypt all land on the email — degraded but
 * correct, and identical to how the app looked before names existed.
 */
export function memberLabel(member: Pick<OrgMember, 'userId' | 'email'>, names: Map<string, string>): string {
  const name = names.get(member.userId)?.trim();
  if (name) return name;
  return member.email || member.userId;
}

/**
 * Decrypt every member's display name for one family.
 *
 * Returns userId → name, omitting members with no name and those whose blob
 * could not be decrypted. Re-runs when the family key hydrates (`version`),
 * which is why callers see emails first and names a moment later rather than a
 * blank roster.
 *
 * `members` is an effect dependency BY REFERENCE. Pass a stable array (state,
 * or a value that only changes when membership actually changes) — passing an
 * inline `.filter()`/`.map()`/`.slice()` result creates a new array on every
 * render, which re-runs the decrypt effect every render too and flickers the
 * roster back to emails in between. If you only have an unstable array,
 * memoize it (e.g. `useMemo`) before passing it in.
 */
export function useMemberNames(
  orgId: string | undefined,
  members: OrgMember[],
  org: Pick<Organization, 'publicKey'> | undefined,
  keys: FamilyKeyAccess,
): Map<string, string> {
  // Seed synchronously from the session memo so a re-mount paints names on its
  // very first render instead of flashing emails and correcting itself.
  const [names, setNames] = useState<Map<string, string>>(
    () => (orgId && decryptedMemo.get(orgId)) || new Map(),
  );

  useEffect(() => {
    if (!orgId || !org?.publicKey) return;
    const priv = keys.getKey(orgId);
    if (!priv) {
      // Key gone (lock / logout / user switch): drop both the memo and what we
      // are showing. Holding either would surface the previous user's names.
      decryptedMemo.delete(orgId);
      setNames(new Map());
      return;
    }
    const pub = base64UrlToBytes(org.publicKey);

    // Prefer the live roster; fall back to the persisted ciphertext so a page
    // that mounts before its getOrg lands still has something to decrypt.
    const source: Array<Pick<OrgMember, 'userId'> & {
      encryptedName?: string; encryptedNameIv?: string; encryptedNameEphemeralPublicKey?: string;
    }> = members.length > 0 ? members : (getCachedMemberNames(orgId) ?? []);

    // Only the server's own ciphertext is persisted — never a decrypted name.
    if (members.length > 0) setCachedMemberNames(orgId, members);

    let cancelled = false;
    void (async () => {
      const next = new Map<string, string>();
      for (const m of source) {
        if (!m.encryptedName) continue;
        try {
          // Per-member catch: one corrupt blob must cost exactly one name, not
          // the whole roster. This codebase has shipped the opposite twice.
          next.set(m.userId, await decryptMemberName(m, priv, pub));
        } catch {
          /* fall back to this member's email */
        }
      }
      if (cancelled) return;
      decryptedMemo.set(orgId, next);
      setNames(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, org?.publicKey, members, keys.version]);

  return names;
}
